/*
  About page motion: IntersectionObserver reveals + lightweight parallax.
  Initializes on the live storefront. No scroll hijacking.
*/

(function () {
  'use strict';

  const getTheme = () => window.HouseOfThiraa || null;

  const prefersReducedMotion = () => {
    const theme = getTheme();
    if (theme?.responsive?.matches) {
      return theme.responsive.matches('motionReduce');
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const desktopQuery = window.matchMedia('(min-width: 64rem)');

  const instances = new WeakMap();

  class AboutPageMotion {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.parallaxItems = [];
      this.raf = 0;
    }

    init() {
      if (prefersReducedMotion() || this.root.dataset.aboutAnimate !== 'true') {
        this.root.querySelectorAll('[data-about-reveal]').forEach((node) => node.classList.add('is-in'));
        return;
      }

      this.observeReveals();
      this.collectParallax();
      this.startParallax();
    }

    observeReveals() {
      const nodes = Array.from(this.root.querySelectorAll('[data-about-reveal]'));
      if (!nodes.length) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            const node = entry.target;
            const delay = Number(node.dataset.aboutDelay || 0);
            node.style.setProperty('--about-delay', String(delay));
            if (node.dataset.aboutY) {
              node.style.setProperty('--about-y', `${node.dataset.aboutY}px`);
            }
            node.classList.add('is-in');
            this.observer.unobserve(node);
          });
        },
          { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
      );

      nodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.bottom < 64) {
          node.classList.add('is-in');
          return;
        }
        this.observer.observe(node);
      });
    }

    collectParallax() {
      this.parallaxItems = Array.from(this.root.querySelectorAll('[data-about-parallax]')).map((node) => {
        const amount = Number(node.dataset.aboutParallax || 24);
        const capped = Math.sign(amount) * Math.min(Math.abs(amount), 28);
        return {
          frame: node,
          node: node.querySelector('.about-page__image') || node,
          amount: Number.isFinite(capped) ? capped : 24
        };
      });
    }

    startParallax() {
      if (!this.parallaxItems.length || prefersReducedMotion()) {
        return;
      }

      const { signal } = this.abort;

      const update = () => {
        this.raf = 0;
        if (!desktopQuery.matches) {
          this.parallaxItems.forEach(({ node }) => {
            node.style.transform = '';
          });
          return;
        }

        this.parallaxItems.forEach(({ frame, node, amount }) => {
          if (frame.hasAttribute('data-about-reveal') && !frame.classList.contains('is-in')) {
            return;
          }
          const rect = node.getBoundingClientRect();
          if (rect.bottom < -80 || rect.top > (window.innerHeight || 0) + 80) {
            return;
          }
          const view = window.innerHeight || 1;
          const progress = (view - rect.top) / (view + rect.height) - 0.5;
          const y = Math.max(-amount, Math.min(amount, progress * amount * 2));
          node.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
        });
      };

      const requestTick = () => {
        if (this.raf) {
          return;
        }
        this.raf = window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', requestTick, { signal, passive: true });
      window.addEventListener('resize', requestTick, { signal, passive: true });
      requestTick();
    }

    destroy() {
      this.abort.abort();
      this.observer?.disconnect();
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-about-page]').forEach((root) => {
      if (root.dataset.aboutPageReady === 'true') {
        return;
      }
      root.dataset.aboutPageReady = 'true';
      const motion = new AboutPageMotion(root);
      instances.set(root, motion);
      motion.init();
    });
  };

  const start = () => {
    init();
    const theme = getTheme();
    theme?.events?.on('section:load', (event) => {
      event.detail.target.querySelectorAll('[data-about-page]').forEach((root) => {
        delete root.dataset.aboutPageReady;
      });
      init(event.detail.target);
    });
    theme?.events?.on('section:unload', (event) => {
      event.detail.target.querySelectorAll('[data-about-page]').forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });
    });
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-about-page]').forEach((root) => {
        delete root.dataset.aboutPageReady;
      });
      init(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[data-about-page]').forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
